import { GroupedTimeSeriesEntry } from '@unistocks/shared/charts/chart-data';
import { GroupTimeSeriesEntryKey } from '@unistocks/shared/charts/chart-data/group';
import { useGroupData } from '@unistocks/shared/charts/use-group-data';
import { TimePeriod } from '@unistocks/types';
import { TimeSeriesEntry } from '@unistocks/types/time-series';
import { LineChartPoint } from './types';

export type UseLineChartDataOptions<T extends TimeSeriesEntry = LineChartPoint> = {
  /** Raw (ungrouped) series entries, ascending by timestamp */
  entries: T[];
  /** Which grouped value to render. Defaults to 'close'. */
  groupValue?: GroupTimeSeriesEntryKey;
};

export type UseLineChartDataResult<T extends TimeSeriesEntry = LineChartPoint> = {
  /** Grouped chart data for rendering */
  chartData: GroupedTimeSeriesEntry<T>[];
  /** The time period grouping applied to the data */
  groupBy: TimePeriod;
  /** First value (initial) used for percentage calculations */
  initialValue: number;
  /** First date timestamp in the data */
  firstDate: number | undefined;
  /** Last date timestamp in the data */
  lastDate: number | undefined;
};

/**
 * Hook to group a raw time series for line chart rendering and resolve
 * the shared scale metadata (initial value, first/last date).
 * Shared between native and web implementations.
 */
export const useLineChartData = <T extends TimeSeriesEntry = LineChartPoint>({
  entries,
  groupValue = 'close'
}: UseLineChartDataOptions<T>): UseLineChartDataResult<T> => {
  const { values: chartData, groupBy } = useGroupData<T>(entries, { groupValue });

  const initialValue = entries[0]?.value ?? 0;
  const firstDate = entries[0]?.timestamp;
  const lastDate = entries.at(-1)?.timestamp;

  return {
    chartData,
    groupBy,
    initialValue,
    firstDate,
    lastDate
  };
};
